// Admin: PayFast ITN webhook log filtered by invoice reference — signature
// validation result, processing result, and any failures per invoice.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ShieldCheck, ShieldAlert, Webhook } from "lucide-react";
import { adminListPayfastInvoiceWebhookEvents } from "@/lib/api/invoice-reminders.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/payfast-invoice-log",
)({
  head: () => ({ meta: [{ title: "Admin · PayFast invoice webhook log" }] }),
  component: PayfastInvoiceLogPage,
});

type OutcomeFilter = "all" | "granted" | "failed" | "invalid_signature";

function PayfastInvoiceLogPage() {
  const list = useServerFn(adminListPayfastInvoiceWebhookEvents);
  const [reference, setReference] = useState("");
  const [outcome, setOutcome] = useState<OutcomeFilter>("all");
  const [limit, setLimit] = useState(50);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin-pf-invoice-log", reference, outcome, limit],
    queryFn: () => list({ data: { reference, outcome, limit } }),
    refetchInterval: 60_000,
  });

  const events = data?.events ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-navy flex items-center gap-2">
            <Webhook className="h-5 w-5" /> PayFast invoice webhook log
          </h1>
          <p className="text-sm text-muted-foreground">
            Every ITN callback captured for a manual invoice, including signature validation and grant outcome.
          </p>
        </div>
        <Link
          to="/portal/admin/invoice-reconciliation"
          className="text-xs text-primary underline"
        >
          Reconciliation →
        </Link>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[220px]">
              <label className="text-[11px] uppercase text-muted-foreground">Invoice reference</label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="INV-…" />
            </div>
            <div>
              <label className="text-[11px] uppercase text-muted-foreground block">Outcome</label>
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as OutcomeFilter)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="all">All</option>
                <option value="granted">Granted</option>
                <option value="failed">Failed / error</option>
                <option value="invalid_signature">Invalid signature</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase text-muted-foreground block">Limit</label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className="mr-1 h-3 w-3" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Events · {events.length}</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading…</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events match this filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b text-left uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Timestamp</th>
                    <th className="py-2 pr-3">Reference</th>
                    <th className="py-2 pr-3">PayFast ID</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Signature</th>
                    <th className="py-2 pr-3">Match</th>
                    <th className="py-2 pr-3">Grant</th>
                    <th className="py-2 pr-3">ms</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {events.map((e: any) => (
                    <tr key={e.id}>
                      <td className="py-1.5 pr-3 font-mono">{new Date(e.created_at).toLocaleString("en-ZA")}</td>
                      <td className="py-1.5 pr-3 font-mono">{e.reference ?? "—"}</td>
                      <td className="py-1.5 pr-3 font-mono">{e.pf_payment_id ?? "—"}</td>
                      <td className="py-1.5 pr-3">{e.payment_status ?? "—"}</td>
                      <td className="py-1.5 pr-3">
                        {e.signature_valid ? (
                          <span className="inline-flex items-center text-emerald-700"><ShieldCheck className="mr-1 h-3 w-3" />valid</span>
                        ) : (
                          <span className="inline-flex items-center text-rose-700"><ShieldAlert className="mr-1 h-3 w-3" />invalid</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">{e.match_kind ?? "—"}</td>
                      <td className="py-1.5 pr-3">
                        {e.grant_status ? (
                          <Badge variant={e.grant_status === "granted" ? "default" : "secondary"}>{e.grant_status}</Badge>
                        ) : "—"}
                        {e.grant_error ? <div className="text-rose-700">{e.grant_error}</div> : null}
                      </td>
                      <td className="py-1.5 pr-3">{e.processing_ms ?? "—"}</td>
                      <td className="py-1.5 pr-3">{e.mode ?? "—"}</td>
                      <td className="py-1.5 pr-3 max-w-[220px] truncate" title={e.notes ?? ""}>{e.notes ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
